/**
 * @file readOnlyFetch.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-10-15
 *
 * 1.2.0 (2026-10-15): while a planning board is open, requests to
 * /api/transport/ carry X-Planning-Board and X-Planning-Tab (utils/
 * planningControl) so the server can keep the board to one planner; a write
 * it refuses for that (409 PLANNING_CONTROL) is said in a sentence and the
 * board is told to look again (PLANNING_CONTROL_EVENT).
 *
 * Turns the server's read-only refusal into a sentence, everywhere, once.
 *
 * middleware/readOnly.js blocks every write from a view-only account with a
 * 403 carrying `code: 'READ_ONLY'`. Individual screens handle a failed save in
 * their own way — some show the error, some log it, a few do neither — so
 * without this a rep could press a button and watch nothing happen. Wrapping
 * window.fetch means the explanation appears no matter which of the two hundred
 * call sites made the request.
 *
 * This is a safety net, not the design. The intent is that a view-only account
 * is not offered the button in the first place; screens mark their write
 * controls so `[data-readonly]` can disable them (see App.css). When one is
 * missed, this is what the user sees instead of silence.
 *
 * It also raises `excelAgri:session-expired` when the server stops recognising
 * the caller. A 401 can arrive at any moment — the cookie has a lifetime and it
 * runs out mid-afternoon — and until this existed nothing noticed: the page
 * carried on looking signed in while every request failed, and the user found
 * out by watching a save do nothing. Catching it here means it is noticed
 * whichever of the two hundred call sites happened to be next.
 *
 * Installed once from index.js. Idempotent — a second call is a no-op — so a
 * hot reload does not stack wrappers.
 */

import { showAlert } from '../components/Alert';
import { planningHeaders, PLANNING_CONTROL_EVENT } from './planningControl';

const INSTALLED = Symbol.for('excelAgri.readOnlyFetchInstalled');

/** The event a 401 raises. Dashboard listens; anything else may too. */
export const SESSION_EXPIRED_EVENT = 'excelAgri:session-expired';

/*
 * The endpoints where a 401 means "not signed in" rather than "not you".
 *
 * Only same-origin /api calls count. A 401 from Google, Xero's own domain or
 * any third party says nothing about OUR session, and treating it as expiry
 * would throw the user out over somebody else's outage.
 */
function isOurApi(input) {
  try {
    const url = typeof input === 'string' ? input
      : (input instanceof Request ? input.url : String(input?.url || input));
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.startsWith('/api/');
  } catch (_) {
    return false;
  }
}

/*
 * WHERE a request came from, for the audit trail: the screen, and the window
 * open on it — "Transport Planning", "Search Purchase Orders › Send Purchase
 * Order PO-D10220". Sent as X-App-Source on every request to our API; the
 * server stores it on each audit and email row it writes during that request
 * (utils/requestContext.js). Dashboard keeps the screen name on <body>.
 * Encoded, because a header must be ASCII and titles carry "—" and "›".
 */
function currentSource() {
  try {
    const screen = document.body?.dataset?.screen || '';
    const titles = [...document.querySelectorAll('.modal-content .modal-header h2, .modal-content .modal-header h3')]
      .filter(el => el.offsetParent !== null)
      .map(el => el.textContent.trim())
      .filter(Boolean);
    const top = titles[titles.length - 1];
    return [screen, top].filter(Boolean).join(' › ').slice(0, 200);
  } catch {
    return '';
  }
}

function pathOf(input) {
  try {
    const url = typeof input === 'string' ? input
      : (input instanceof Request ? input.url : String(input?.url || input));
    return new URL(url, window.location.origin).pathname;
  } catch (_) {
    return '';
  }
}

function withSource(args) {
  if (!isOurApi(args[0])) return args;
  const source = currentSource();
  const planning = planningHeaders(pathOf(args[0]));
  if (!source && !planning) return args;
  const [input, init = {}] = args;
  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  if (source) headers.set('X-App-Source', encodeURIComponent(source));
  if (planning) for (const [k, v] of Object.entries(planning)) headers.set(k, v);
  return [input, { ...init, headers }];
}

export function installReadOnlyFetchGuard() {
  if (window[INSTALLED]) return;
  window[INSTALLED] = true;

  const originalFetch = window.fetch.bind(window);
  let lastAlertAt = 0;
  let lastExpiryAt = 0;

  window.fetch = async (...rawArgs) => {
    const args = withSource(rawArgs);
    const response = await originalFetch(...args);

    // 403 is common and mostly means "you lack this permission", which callers
    // report themselves. Only the read-only refusal is announced here, and only
    // once every few seconds — a bulk action can fire twenty of these and
    // twenty identical toasts is worse than none.
    /*
     * The session is gone. Announced once — a screen that fires six requests on
     * load would otherwise raise six — and the listener decides what to do; the
     * fetch layer has no business redirecting anybody.
     *
     * The login endpoint is excluded: a wrong password is a 401 and is not an
     * expired session.
     */
    if (response.status === 401 && isOurApi(args[0])
        && !String(args[0]).includes('/api/users/login')
        && Date.now() - lastExpiryAt > 5000) {
      lastExpiryAt = Date.now();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    if (response.status === 403) {
      try {
        const clone = response.clone();
        const data = await clone.json();
        if (data?.code === 'READ_ONLY' && Date.now() - lastAlertAt > 4000) {
          lastAlertAt = Date.now();
          showAlert(data.detail || data.error || 'Your account has view-only access', 'warning');
        }
      } catch (_) {
        // Not JSON, or already consumed — nothing to report.
      }
    }

    // One planner at a time: the board this tab is on is held by someone else.
    if (response.status === 409 && isOurApi(args[0])) {
      try {
        const data = await response.clone().json();
        if (data?.code === 'PLANNING_CONTROL') {
          window.dispatchEvent(new CustomEvent(PLANNING_CONTROL_EVENT, { detail: data }));
          if (Date.now() - lastAlertAt > 4000) {
            lastAlertAt = Date.now();
            showAlert(data.error || 'Someone else is planning this board', 'warning');
          }
        }
      } catch (_) {
        // Not JSON — nothing to report.
      }
    }

    return response;
  };
}

export default installReadOnlyFetchGuard;
